/**
 * Proactive AI Message Component
 *
 * Displays a subtle notification bubble when the AI wants to
 * proactively engage the customer. Non-blocking and easy to dismiss.
 */

import { useState, useEffect } from 'react';

// Animation timing constants (in milliseconds)
const ANIMATION_ENTER_DELAY_MS = 100;
const ANIMATION_EXIT_DURATION_MS = 300;
const ANIMATION_ENGAGE_DURATION_MS = 200;
const DEFAULT_AUTO_HIDE_DELAY_MS = 15000;

interface ProactiveMessageProps {
  message: string;
  triggerId: string;
  onEngage: () => void;
  onDismiss: (triggerId: string) => void;
  autoHideDelay?: number; // ms to auto-hide if not interacted with
}

export function ProactiveMessage({
  message,
  triggerId,
  onEngage,
  onDismiss,
  autoHideDelay = DEFAULT_AUTO_HIDE_DELAY_MS,
}: ProactiveMessageProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Animate in on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), ANIMATION_ENTER_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Auto-hide after delay
  useEffect(() => {
    if (!autoHideDelay) return;

    const timer = setTimeout(() => {
      handleDismiss();
    }, autoHideDelay);

    return () => clearTimeout(timer);
  }, [autoHideDelay]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(triggerId);
    }, ANIMATION_EXIT_DURATION_MS);
  };

  const handleEngage = () => {
    setIsExiting(true);
    setTimeout(() => {
      onEngage();
    }, ANIMATION_ENGAGE_DURATION_MS);
  };

  return (
    <div
      className={`fixed bottom-24 right-6 z-40 max-w-xs transform transition-all duration-300 ${
        isVisible && !isExiting
          ? 'translate-y-0 opacity-100'
          : 'translate-y-4 opacity-0'
      }`}
    >
      {/* Message bubble */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        {/* Header with dismiss button */}
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
              <SparkleIcon className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="text-xs text-gray-500 font-medium">Asistente PetMas</span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Cerrar"
          >
            <CloseIcon className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Message content */}
        <div className="px-4 pb-3">
          <p className="text-sm text-gray-700 leading-relaxed">{message}</p>

          {/* Action button */}
          <button
            onClick={handleEngage}
            className="mt-3 w-full py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition-colors"
          >
            Chatear ahora
          </button>
        </div>

        {/* Progress bar for auto-hide */}
        {autoHideDelay && (
          <div className="h-0.5 bg-gray-100">
            <div
              className="h-full bg-primary/30 animate-shrink"
              style={{
                animation: `shrink ${autoHideDelay}ms linear forwards`,
              }}
            />
          </div>
        )}
      </div>

      {/* Speech bubble tail */}
      <div className="absolute -bottom-2 right-8 w-4 h-4 bg-white border-b border-r border-gray-100 transform rotate-45" />

      {/* Animation keyframes */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

// Minimal proactive notification (just the bubble without full card)
export function ProactiveNotificationBubble({
  message,
  onClick,
  onDismiss,
}: {
  message: string;
  onClick: () => void;
  onDismiss: () => void;
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), ANIMATION_ENTER_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={`fixed bottom-24 right-6 z-40 transform transition-all duration-300 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
    >
      <div className="relative">
        {/* Pulsing ring */}
        <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />

        {/* Main bubble */}
        <button
          onClick={onClick}
          className="relative bg-primary text-white px-4 py-2 rounded-full shadow-lg hover:bg-primary-dark transition-colors flex items-center gap-2"
        >
          <SparkleIcon className="w-4 h-4" />
          <span className="text-sm font-medium max-w-[200px] truncate">{message}</span>
        </button>

        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full shadow-sm flex items-center justify-center hover:bg-gray-100"
          aria-label="Cerrar"
        >
          <CloseIcon className="w-3 h-3 text-gray-500" />
        </button>
      </div>
    </div>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3zm7 14l.75 2.25L22 20l-2.25.75L19 23l-.75-2.25L16 20l2.25-.75L19 17zm-14 0l.75 2.25L8 20l-2.25.75L5 23l-.75-2.25L2 20l2.25-.75L5 17z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}
